import { useCallback } from "react";
import { useLocation } from "react-router";

import { useScopedHref } from "@/hooks";

import { NavLinkItem, NavLinkItemType } from "./NavLink.types";
import { useGetNavLinkPath } from "./useGetNavLinkPath";

const isExternalNavPath = (path: string) =>
  path.startsWith("https://") || path.startsWith("http://") || path.startsWith("mailto:");

// Links that deep-link into a specific tab must still highlight for the whole section.
const NAV_LINK_ACTIVE_BASE_PATHS: Partial<{ [key in NavLinkItemType]: string }> = {
  tournaments: "/tournaments",
};

export const useIsNavLinkActive = () => {
  const location = useLocation();
  const getNavLinkPath = useGetNavLinkPath();
  const scopedHref = useScopedHref();
  const pathname = location.pathname;

  const isActive = useCallback(
    (item: NavLinkItem) => {
      const activeBasePath = NAV_LINK_ACTIVE_BASE_PATHS[item.type];
      const basePath = activeBasePath ? scopedHref(activeBasePath) : getNavLinkPath(item.type);

      if (isExternalNavPath(basePath)) {
        return pathname === basePath;
      }

      // `/` is a prefix of every URL — only exact match for home.
      if (item.type === "home") {
        return pathname === basePath;
      }

      return pathname === basePath || pathname.startsWith(`${basePath}/`);
    },
    [getNavLinkPath, scopedHref, pathname]
  );

  return isActive;
};
